import { DashboardCard } from "./dashboard-card";
import { useAccountContext } from "../account-context";
import { formatCurrency, cn } from "@/lib/utils";
import { Spinner } from "@/components/spinner";
import { parseDecimal } from "@/backend/types";
import { useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { type DateRangeValue } from "@/components/ui/date-range-selector";
import {
  ActivityIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  TrendingUpIcon,
  TrendingDownIcon,
} from "lucide-react";

type ActivityItem = {
  id: string;
  type: "trade" | "deposit" | "withdrawal";
  title: string;
  subtitle: string;
  amount: number;
  timestamp: Date;
  status?: string;
  side?: "buy" | "sell"; // For trades: buy = Long, sell = Short
};

type RecentActivityCardProps = {
  dateRange?: DateRangeValue;
};

export const RecentActivityCard = ({ dateRange: _dateRange }: RecentActivityCardProps) => {
  const { fills, deposits, withdrawals, isLoading } = useAccountContext();

  const activities = useMemo(() => {
    const items: ActivityItem[] = [];

    // Add trades (fills)
    fills.slice(0, 10).forEach((fill) => {
      const size = parseDecimal(fill.size);
      const price = parseDecimal(fill.price);
      items.push({
        id: fill.id,
        type: "trade",
        title: fill.market,
        subtitle: `${fill.side === "buy" ? "Long" : "Short"} ${size.toFixed(4)} @ $${formatCurrency(price, 2)}`,
        amount: size * price,
        timestamp: new Date(fill.created_at),
        side: fill.side as "buy" | "sell",
      });
    });

    // Add deposits
    deposits.slice(0, 5).forEach((deposit) => {
      items.push({
        id: deposit.id,
        type: "deposit",
        title: "Deposit",
        subtitle: `${deposit.status}`,
        amount: parseDecimal(deposit.amount_usd),
        timestamp: new Date(deposit.created_at),
        status: deposit.status,
      });
    });

    // Add withdrawals
    withdrawals.slice(0, 5).forEach((withdrawal) => {
      items.push({
        id: withdrawal.id,
        type: "withdrawal",
        title: "Withdrawal",
        subtitle: `${withdrawal.status}`,
        amount: parseDecimal(withdrawal.amount_usd),
        timestamp: new Date(withdrawal.created_at),
        status: withdrawal.status,
      });
    });

    // Sort by timestamp, most recent first
    items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    return items.slice(0, 10);
  }, [fills, deposits, withdrawals]);

  const getIcon = (activity: ActivityItem) => {
    switch (activity.type) {
      case "trade":
        // TrendingUp for Long (buy), TrendingDown for Short (sell)
        return activity.side === "buy" ? TrendingUpIcon : TrendingDownIcon;
      case "deposit":
        // Arrow down - money coming into account
        return ArrowDownIcon;
      case "withdrawal":
        // Arrow up - money going out of account
        return ArrowUpIcon;
    }
  };

  const getIconColor = (activity: ActivityItem) => {
    switch (activity.type) {
      case "trade":
        // Green for Long (buy), Red for Short (sell)
        return activity.side === "buy" 
          ? "text-bid-foreground bg-bid" 
          : "text-ask-foreground bg-ask";
      case "deposit":
        // Green - money coming in
        return "text-bid-foreground bg-bid";
      case "withdrawal":
        // Red - money going out
        return "text-ask-foreground bg-ask";
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (isLoading) {
    return (
      <DashboardCard title="Recent Activity" subtitle="Latest transactions">
        <div className="flex items-center justify-center h-48">
          <Spinner />
        </div>
      </DashboardCard>
    );
  }

  if (!activities.length) {
    return (
      <DashboardCard title="Recent Activity" subtitle="Latest transactions" className="h-full">
        <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
          <ActivityIcon className="size-12 mb-3 opacity-50" />
          <p className="text-sm">No recent activity</p>
          <p className="text-xs">Your transactions will appear here</p>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard title="Recent Activity" subtitle="Latest transactions" className="h-full">
      <ScrollArea className="h-[280px] -mx-2 px-2">
        <div className="space-y-3">
          {activities.map((activity) => {
            const Icon = getIcon(activity);
            const iconColor = getIconColor(activity);

            return (
              <div
                key={activity.id}
                className="flex items-center gap-3 py-2 border-b border-border/30 last:border-0"
              >
                <div className={cn("p-2 rounded-lg", iconColor)}>
                  <Icon className="size-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">
                      {activity.title}
                    </span>
                    {activity.status && (
                      <span
                        className={cn(
                          "text-xs px-1.5 py-0.5 rounded capitalize",
                          activity.status === "credited" || activity.status === "completed"
                            ? "bg-bid text-bid-foreground"
                            : activity.status === "pending" || activity.status === "processing"
                            ? "bg-yellow-500/10 text-yellow-500"
                            : activity.status === "failed" || activity.status === "rejected"
                            ? "bg-ask text-ask-foreground"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        {activity.status}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {activity.subtitle}
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className={cn(
                      "font-medium text-sm",
                      activity.type === "deposit"
                        ? "text-bid-foreground"
                        : activity.type === "withdrawal"
                        ? "text-ask-foreground"
                        : "text-foreground"
                    )}
                  >
                    {activity.type === "deposit" ? "+" : activity.type === "withdrawal" ? "-" : ""}
                    ${formatCurrency(activity.amount, 2)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatTime(activity.timestamp)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </DashboardCard>
  );
};
